import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, FileText, MessageSquare, User, Check, Loader2, ChevronRight, Sparkles } from 'lucide-react';
import { apiFetch } from '../../api';
import SessionContextPreview from './SessionContextPreview';

/**
 * SessionContextPicker - macOS-inspired popup for pulling session context into AI
 *
 * Features:
 * - Smooth scale+fade animation on open/close
 * - Session list with preview on selection
 * - Data type toggles (summary, transcript, student profile)
 * - Quick workflow optimized for <10 seconds
 */

const SessionContextPicker = ({ onConfirm, onClose, currentContext }) => {
  // Animation state
  const [isClosing, setIsClosing] = useState(false);

  // Data states
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection states
  const [selectedSessionId, setSelectedSessionId] = useState(currentContext?.sessionId || null);
  const [selectedDataTypes, setSelectedDataTypes] = useState({
    summary: true,
    transcript: false,
    studentProfile: true
  });

  // Preview states
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const containerRef = useRef(null);

  // Define handleClose first since useEffects depend on it
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); // Match close animation duration
  }, [onClose]);

  // Fetch sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        const response = await apiFetch('/api/sessions/list');
        setSessions(response.sessions || []);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setError('Failed to load sessions');
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  // Fetch preview when session selected
  useEffect(() => {
    if (!selectedSessionId) {
      setPreviewData(null);
      return;
    }

    const fetchPreview = async () => {
      try {
        setPreviewLoading(true);
        const response = await apiFetch(`/api/sessions/${selectedSessionId}`);
        setPreviewData(response);
      } catch (err) {
        console.error('Failed to fetch session preview:', err);
        setPreviewData(null);
      } finally {
        setPreviewLoading(false);
      }
    };
    fetchPreview();
  }, [selectedSessionId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        // Don't close if clicking the trigger button
        if (e.target.closest('.wb-context-trigger')) return;
        handleClose();
      }
    };
    // Small delay to prevent immediate close on open click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClose]);

  const handleSessionSelect = useCallback((sessionId) => {
    setSelectedSessionId(sessionId);
  }, []);

  const handleDataTypeToggle = useCallback((type) => {
    setSelectedDataTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedSessionId || !previewData) return;

    // Get session name from metadata or summary overview or id
    const sessionName = previewData.metadata?.name
      || previewData.summary?.session_overview?.slice(0, 50)
      || previewData.id
      || selectedSessionId;

    let contextData = {
      sessionId: selectedSessionId,
      sessionName: sessionName,
      timestamp: new Date().toISOString()
    };

    // Add selected data types
    if (selectedDataTypes.summary && previewData.summary) {
      contextData.summary = previewData.summary;
    }

    if (selectedDataTypes.transcript) {
      try {
        const transcriptResponse = await apiFetch(`/api/sessions/${selectedSessionId}/transcript`);
        // Handle nested transcript data - could be { transcript: { scrubbed_transcript: "..." } }
        const transcriptData = transcriptResponse.transcript;
        if (typeof transcriptData === 'string') {
          contextData.transcript = transcriptData;
        } else if (transcriptData?.scrubbed_transcript) {
          contextData.transcript = transcriptData.scrubbed_transcript;
        } else if (transcriptData?.transcript) {
          contextData.transcript = transcriptData.transcript;
        } else {
          contextData.transcript = JSON.stringify(transcriptData);
        }
      } catch (err) {
        console.error('Failed to fetch transcript:', err);
      }
    }

    if (selectedDataTypes.studentProfile && previewData.student) {
      contextData.studentProfile = previewData.student;
    }

    onConfirm(contextData);
    handleClose();
  }, [selectedSessionId, previewData, selectedDataTypes, onConfirm, handleClose]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const hasSelection = selectedSessionId && previewData;
  const hasAnyDataType = Object.values(selectedDataTypes).some(v => v);

  return (
    <div
      ref={containerRef}
      className={`wb-context-picker ${isClosing ? 'closing' : ''}`}
    >
      {/* Header */}
      <div className="wb-context-picker-header">
        <div className="wb-context-picker-title">
          <Sparkles className="wb-context-picker-title-icon" />
          <span>Pull Session Context</span>
        </div>
        <button
          className="wb-context-picker-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <X />
        </button>
      </div>

      {/* Content */}
      <div className="wb-context-picker-content">
        {loading ? (
          <div className="wb-context-picker-loading">
            <Loader2 className="wb-context-spinner" />
            <span>Loading sessions...</span>
          </div>
        ) : error ? (
          <div className="wb-context-picker-error">
            <span>{error}</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="wb-context-picker-empty">
            <span>No sessions available</span>
          </div>
        ) : (
          <>
            {/* Session List */}
            <div className="wb-context-session-list">
              <div className="wb-context-section-label">Recent Sessions</div>
              {sessions.slice(0, 5).map((session) => {
                // Handle both API formats: id or session_id
                const sessionId = session.id || session.session_id;
                const sessionName = session.metadata?.name || session.preview?.overview?.slice(0, 50) || sessionId;
                const sessionDate = session.date || session.metadata?.date || session.timestamp;
                const topics = session.preview?.topics || session.summary?.topics_covered || [];

                return (
                  <button
                    key={sessionId}
                    className={`wb-context-session-item ${selectedSessionId === sessionId ? 'selected' : ''}`}
                    onClick={() => handleSessionSelect(sessionId)}
                  >
                    <div className="wb-context-session-radio">
                      {selectedSessionId === sessionId && <Check />}
                    </div>
                    <div className="wb-context-session-info">
                      <div className="wb-context-session-name">
                        {sessionName}
                      </div>
                      <div className="wb-context-session-meta">
                        {formatDate(sessionDate)}
                        {topics.length > 0 && (
                          <span className="wb-context-session-topics">
                            {topics.slice(0, 2).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="wb-context-session-chevron" />
                  </button>
                );
              })}
            </div>

            {/* Data Type Toggles */}
            <div className="wb-context-data-toggles">
              <div className="wb-context-section-label">Include in Context</div>
              <div className="wb-context-toggle-group">
                <button
                  className={`wb-context-toggle ${selectedDataTypes.summary ? 'active' : ''}`}
                  onClick={() => handleDataTypeToggle('summary')}
                >
                  <FileText />
                  <span>Summary</span>
                </button>
                <button
                  className={`wb-context-toggle ${selectedDataTypes.transcript ? 'active' : ''}`}
                  onClick={() => handleDataTypeToggle('transcript')}
                >
                  <MessageSquare />
                  <span>Transcript</span>
                </button>
                <button
                  className={`wb-context-toggle ${selectedDataTypes.studentProfile ? 'active' : ''}`}
                  onClick={() => handleDataTypeToggle('studentProfile')}
                >
                  <User />
                  <span>Student</span>
                </button>
              </div>
            </div>

            {/* Preview */}
            {selectedSessionId && (
              <div className="wb-context-preview-section">
                <div className="wb-context-section-label">Preview</div>
                {previewLoading ? (
                  <div className="wb-context-preview-loading">
                    <Loader2 className="wb-context-spinner" />
                  </div>
                ) : previewData ? (
                  <SessionContextPreview
                    data={previewData}
                    selectedTypes={selectedDataTypes}
                  />
                ) : null}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="wb-context-picker-footer">
        <button
          className="wb-context-btn wb-context-btn-cancel"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          className="wb-context-btn wb-context-btn-confirm"
          onClick={handleConfirm}
          disabled={!hasSelection || !hasAnyDataType}
        >
          <Sparkles />
          <span>Add to Context</span>
        </button>
      </div>
    </div>
  );
};

export default SessionContextPicker;
